import { useQuery } from '@tanstack/vue-query'
import financialBff from '~/services-v2/financial-bff/financial-bff'
import useFinancing from '~/store-v2/financings/useFinancing'
import useHardwareHistory from '~/store-v2/installation/useHardwareHistory'

/**
 * Checagem de TAF na instalação nova: mesma regra do usePendingUpdateEquipment,
 * sem polling de financingV2 nem formalization (só getCommissioningStatus deve repetir).
 */
export function useInstallationPendingEquipment(projectId: Ref<string>) {
  const { data: financing } = useFinancing(projectId, false)

  const isContractStepAvailable = computed(() => {
    return !!financing.value?.openable_steps?.contract
  })

  const { data: hardwareHistory, isLoading: isLoadingHistory, refetch: refetchHardwareHistory } = useHardwareHistory(projectId)

  const { data: formalizationTaf } = useQuery({
    queryKey: computed(() => ['INSTALLATION', 'PENDING_EQUIPMENT_TAF', projectId.value]),
    enabled: () => isContractStepAvailable.value && Boolean(projectId.value),
    refetchInterval: false,
    queryFn: async () => {
      const res = await financialBff('query')({
        formalization: [{ projectId: projectId.value }, {
          contracts: {
            financing_amendment_term: {
              status: true,
            },
          },
        }],
      })
      return res.formalization
    },
  })

  const pendingUpdateEquipment = computed(() => {
    if (isLoadingHistory.value) return null

    const history = hardwareHistory.value?.data?.history
    if (!history || history.length === 0) return null

    return history.find(item => item.status === 'pending') || null
  })

  const hasTAFInProgress = computed(() => {
    const tafContracts = formalizationTaf.value?.contracts?.financing_amendment_term as
      | Array<{ status?: string }>
      | { status?: string }
      | null
      | undefined

    if (!tafContracts || (Array.isArray(tafContracts) && tafContracts.length === 0)) {
      return false
    }

    const pendingStatuses = ['in_progress', 'waiting']

    if (Array.isArray(tafContracts)) {
      return tafContracts.some(taf => pendingStatuses.includes(taf?.status || ''))
    }

    return pendingStatuses.includes(tafContracts?.status || '')
  })

  const hasPendingTAF = computed(() => {
    return Boolean(pendingUpdateEquipment.value) || hasTAFInProgress.value
  })

  watch(hasTAFInProgress, (newValue, oldValue) => {
    if (oldValue && !newValue) {
      refetchHardwareHistory()
    }
  })

  return {
    pendingUpdateEquipment,
    hasPendingTAF,
    isLoading: isLoadingHistory,
  }
}
